import * as ko from "knockout";
import {IMessageData, IObjectData, IResponse} from '../../../index';
import {global, lm} from "../../../appContext";
import {KOSubscriptionTracker} from "../../../util/KOSubscriptionTracker";
import {ObjectTypeId, TEXT_WRAPPER_END, TEXT_WRAPPER_START} from "../../../constants";
import {createErrorMessage, getObjectDefinition} from "../../../util/misc";
import pubsub from "../../../pubsub";
import {IPreviewParams} from "./builder";
import previewHtml from './preview.html?raw';

export let template = previewHtml;

export class viewModel {
    private readonly evaluation: KnockoutObservable<string | null> = ko.observable(null);
    private readonly expression: KnockoutObservable<string>;
    private readonly isWaiting: KnockoutObservable<boolean> = ko.observable(false);
    private readonly kosub: KOSubscriptionTracker = new KOSubscriptionTracker();
    private readonly objectData: KnockoutObservable<IObjectData | null> = ko.observable(null);
    private readonly objectDisplayName: KnockoutObservable<string | null> = ko.observable(null);
    private readonly objectTypeId: KnockoutObservable<ObjectTypeId | null>;
    private readonly objectid: KnockoutObservable<number | null>;
    private readonly reqEvaluateexpression = global.getPromiseRequest('evaluateexpression');
    private readonly reqSearchObjectIds = global.getPromiseRequest('searchobject');
    private throttleTimeoutId!: number;
    private readonly messageDataError: KnockoutObservable<IMessageData | null> = ko.observable(null)

    constructor(params: IPreviewParams) {
        this.expression = params.expression;
        this.objectid = params.objectid;
        this.objectTypeId = params.objectTypeId;
        params.fetchEvaluation.fn = this.fetchEvaluation.bind(this)

        this.kosub.subscribeChanged(this.objectid, () => {
            this.messageDataError(null)
            this.evaluation(null)
            this.validateBeforeSubmit()
        })
        this.kosub.subscribeChanged(this.expression, (exp) => {
            this.messageDataError(null)
            if (!exp) {
                this.evaluation(null)
            }
        })
    }

    private validateBeforeSubmit() {
        if (this.objectTypeId() && this.expression() && this.objectid()) {
            clearTimeout(this.throttleTimeoutId);
            this.isWaiting(!!this.expression())
            this.throttleTimeoutId = setTimeout(() => {
                this.fetchEvaluation();
            }, 500);
        } else {
            this.evaluation(null)
        }
    }

    private objectDefinition = ko.pureComputed(() => {
        return getObjectDefinition(this.objectTypeId());
    })

    private fetchEvaluation(): Promise<void> {
        this.messageDataError(null);
        this.reqEvaluateexpression.abort();

        const isValidExpression = this.validateExpression();

        if (!isValidExpression) {
            this.messageDataError({message: 'invalid expression', type: 'warning'})
        }

        if (!isValidExpression || !this.expression()) {
            this.isWaiting(false)
            return Promise.resolve();
        }

        this.isWaiting(true)

        return this.reqEvaluateexpression
            .request({
                expression: this.expression(),
                objectid: this.objectid(),
            }, true, undefined, {errorHandler: this.onError.bind(this)})
            .then((resp: IResponse) => {
                if (parseInt(resp.returncode)) {
                    throw (resp);
                }

                const evaluated = resp.data[0];
                if (evaluated === '') {
                    this.evaluation(lm.t('[((empty string))]'));
                } else if (!evaluated) {
                    this.evaluation(`—`);
                } else {
                    this.evaluation(evaluated);
                }

                this.isWaiting(false)
            })
            .catch((resp: IResponse) => {
                if (resp.returncode) {
                    this.messageDataError(createErrorMessage(resp.lasterror));
                }
                this.evaluation(null);
                this.isWaiting(false)
            });
    }

    private onError(jqXHR: JQueryXHR, textStatus: string, errorThrown: string) {
        this.messageDataError({message: 'invalid expression', type: 'warning'})
    }

    private validateExpression(): boolean {
        if (!this.expression()?.length) {
            return false;
        }
        let i: number = 0;
        let counter: number = 0;
        let char;
        while ((char = this.expression().charAt(i++)) !== "") {
            switch (char) {
                case TEXT_WRAPPER_START:
                    counter++;
                    break;
                case TEXT_WRAPPER_END:
                    counter--;
                    break;
            }
        }
        return counter === 0;
    }

    private messageData = ko.pureComputed((): IMessageData | null => {
        if (this.isWaiting()) {
            return null
        }
        if (this.messageDataError()) {
            return this.messageDataError()
        }
        if (!this.objectid()) {
            return {message: 'awaiting context|td'}
        }
        if (!this.expression()) {
            return {message: 'awaiting expression|td'}
        }
        if (!this.evaluation()) {
            return {message: 'awaiting execution|td'}
        }
        if (!this.validateExpression()) {
            return {message: 'invalid expression', type: 'warning'}
        }
        return null
    })

    dispose() {
        pubsub.off(this);
        this.kosub.unsuscribeAll();
        this.reqSearchObjectIds.abort();
        this.reqEvaluateexpression.abort();
    }
}
